/**
 * @file Represents a ranking table for portfolios based on their scores.
 * The table provides visual indicators for specific portfolio rankings,
 * and hides certain columns on small screen sizes for responsiveness.
 * Special visual cues are given to certain named portfolios.
 */
import {
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { TableVirtuoso } from 'react-virtuoso';
import { STATUS } from '../../functions/visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const StyledDot = styled(Box)(() => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    margin: 'auto',
    position: 'relative',
    zIndex: 1 //prevent dot getting overlapped when row selected
}));

const StyledTableContainer = styled(Box)(({ theme }) => ({
    overflowX: 'hidden',
    aspectRatio: '1/1',
    [theme.breakpoints.down('sm')]: {
        //So bottom of table doesn't get cut off on mobile
        aspectRatio: 'auto',
        overflowX: 'visible'
    }
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
    borderColor: theme.palette.rankingTable.border,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: 0,
    paddingRight: 0,
    textOverflow: 'ellipsis'
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(1.4),
    paddingBottom: theme.spacing(1.4),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
}));

const PortfolioNameCell = styled(StyledTableCell)(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block'
}));

const hiddenOnSmallScreens = {
    display: {
        xs: 'table-cell',
        sm: 'none',
        md: 'table-cell'
    }
};

const hiddenOnMobile = {
    display: {
        xs: 'none',
        sm: 'table-cell'
    }
};

const ROW_STYLE = Object.freeze({
    Default: 0,
    Hidden: 1,
    Highlighted: 2,
    Popped: 3,
    Faded: 4
});

/**
 * Evaluates row style status value based on currently selected row or row's visibility status.
 * Possible values are as follows:
 *  - Default: No row selected, row is visible/open/owned - no additional style
 *  - Hidden: Row is a hidden portfolio - font color to be slightly greyed
 *  - Highlighted: Row is currently selected (via row or dot click) - background color to be set
 *  - Popped: Row is currently selected (via row kebab click) - shadow to be applied to bottom of row
 *  - Faded: A different row is currently selected - font color to be heavily greyed, opacity applied to dot/kebab
 * @param {Object} row - row to be styled
 * @param {Object} rowSelection - state tracking currently selected row
 * @returns {Number} - row style status value
 */
function getRowStyleStatus(row, rowSelection) {
    if (!rowSelection) {
        return row.status === STATUS.hidden
            ? ROW_STYLE.Hidden
            : ROW_STYLE.Default;
    }

    if (row.publicToken !== rowSelection.row.publicToken) {
        return ROW_STYLE.Faded;
    }

    return rowSelection.isKebabClick ? ROW_STYLE.Popped : ROW_STYLE.Highlighted;
}

/**
 * Get cell font color from global theme depending on style status of the given row
 * @param {Number} rowStatus - style status for the row (one of ROW_STYLE options)
 * @param {Object} theme - global theme object
 * @returns {string} - color value for cell's font
 */
function getCellFontColor(rowStatus, theme) {
    switch (rowStatus) {
        case ROW_STYLE.Faded:
            return theme.palette.rankingTable.faded;
        case ROW_STYLE.Hidden:
            return theme.palette.rankingTable.hidden;
        default:
            return theme.palette.common.black;
    }
}

/* Mapping between TableVirtuoso and MUI table components */
const TableComponents = {
    Scroller: forwardRef((props, ref) => (
        <StyledTableContainer {...props} ref={ref} />
    )),
    Table: (props) => (
        <Table
            {...props}
            sx={{ tableLayout: 'fixed', borderCollapse: 'separate' }}
        />
    ),
    TableHead,
    TableRow: (props) => (
        <TableRow
            {...props}
            hover={!props.context.rowSelection}
            sx={{
                boxShadow:
                    getRowStyleStatus(
                        props.item,
                        props.context.rowSelection
                    ) === ROW_STYLE.Popped && '0px 4px 4px 0px #00000040'
            }}
            onMouseOver={() => props.context.onRowMouseOver?.(props.item)}
            onMouseOut={() => props.context.onRowMouseOut?.(props.item)}
            onClick={(event) => props.context.onRowClick?.(event, props.item)}
            data-analyticsid='ebab-table-row'
        />
    ),
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
};

TableComponents.Scroller.displayName = 'Scroller';
TableComponents.TableBody.displayName = 'TableBody';

TableComponents.TableRow.propTypes = {
    context: PropTypes.object,
    item: PropTypes.object
};

/**
 * Renders the ranking table's fixed header row
 * @returns {React.ReactElement}
 */
const FixedHeaderContent = () => {
    return (
        <TableRow>
            <StyledTableHeaderCell width='4%' sx={hiddenOnSmallScreens} />
            <StyledTableHeaderCell align='center' width='10%'>
                Rank
            </StyledTableHeaderCell>
            <StyledTableHeaderCell
                width='56%'
                sx={{ paddingLeft: 1 }}
                align='left'
            >
                Portfolio
            </StyledTableHeaderCell>
            <StyledTableHeaderCell align='center' width='20%'>
                BA Score
            </StyledTableHeaderCell>
            <StyledTableHeaderCell
                align='center'
                width='10%'
                sx={hiddenOnMobile}
            />
        </TableRow>
    );
};

/**
 * Fixed table footer providing gradual fade out of overflowing rows
 * @returns {React.ReactElement}
 */
const FixedFooterContent = () => (
    <TableRow height={30}>
        <TableCell
            size='small'
            sx={{
                position: 'absolute',
                height: '100%',
                width: '100%',
                bottom: -3,
                border: 0,
                background:
                    'linear-gradient(rgba(255,255,255,0), rgba(255,255,255,1) 65%)'
            }}
        />
    </TableRow>
);

/**
 * Renders a single ranking table row
 * @param {Number} _index - row index
 * @param {Object} row - row data
 * @param {Number} rowStyleStatus - style status for the row (one of ROW_STYLE options)
 * @param {Function} handleKebabClick - function called when more info kebab clicked
 * @param {Object} theme - Global theme
 * @returns {React.ReactElement}
 */
const ItemContent = (_index, row, rowStyleStatus, handleKebabClick, theme) => {
    const sharedCellStyle = {
        color: getCellFontColor(rowStyleStatus, theme),
        backgroundColor:
            rowStyleStatus === ROW_STYLE.Highlighted &&
            theme.palette.rankingTable.selected
    };

    return (
        <>
            <StyledTableCell
                id='dotsTableCell'
                align='right'
                sx={{ ...sharedCellStyle, ...hiddenOnSmallScreens }}
            >
                <StyledDot
                    style={{
                        background: row.dotColor,
                        opacity: rowStyleStatus === ROW_STYLE.Faded && 0.3
                    }}
                    data-testid='rankingTableDot'
                />
            </StyledTableCell>
            <StyledTableCell
                id='rankTableCell'
                align='center'
                style={sharedCellStyle}
            >
                {row.rank}
            </StyledTableCell>
            <PortfolioNameCell
                id='portfolioNameTableCell'
                align='left'
                style={sharedCellStyle}
            >
                {row.portfolioLongName}
            </PortfolioNameCell>
            <StyledTableCell
                id='baScoreTableCell'
                align='center'
                style={sharedCellStyle}
            >
                {row.baScore?.toFixed(1)}
            </StyledTableCell>
            <TableCell
                sx={{ ...sharedCellStyle, ...hiddenOnMobile, padding: 0 }}
            >
                <IconButton
                    aria-label='more'
                    id='moreKebab'
                    onClick={(event) => {
                        handleKebabClick(
                            event.currentTarget.parentNode.parentNode,
                            row
                        );
                    }}
                    sx={{ padding: 0, width: '70%', aspectRatio: '1/1' }}
                >
                    <MoreVertIcon
                        sx={{
                            fontSize: '1.6rem',
                            fill: 'black',
                            opacity:
                                rowStyleStatus === ROW_STYLE.Faded ? 0.2 : 0.54
                        }}
                    />
                </IconButton>
            </TableCell>
        </>
    );
};

/**
 * The `RankingTable` component displays a list of portfolios ranked by BA score.
 * It provides a colored dot indicator based on each portfolio's visibility status,
 * while also conditionally hiding these indicators on small screens. The table offers a sticky header.
 * On desktop screen sizes (sm and above), a more info kebab is rendered for each row which opens a Menu.
 *
 * The table is virtualized (using react-virtuoso) so that we only render what is currently visible to the user.
 * This also makes the table to chart interactions a lot faster.
 *
 * @component
 * @param {Array<Object>} filteredData - Filtered EBAB data to display in table
 * @param {Object} selectedPortfolio - Externally selected portfolio (e.g from chart dot)
 * @param {Function} onRowMouseOver - Row mouse-over behaviour
 * @param {Function} onRowMouseOut - Row mouse-out behaviour
 * @param {Function} onRowClick - Row click behaviour
 * @param {Function} onKebabClick - Row kebab icon click behaviour
 * @param {Object} selectedRow - Row selected from table itself (for desktop)
 * @param {Object} tableVirtuosoProps - Props passed to TableVirtuoso component
 * @returns {React.ReactElement} The rendered RankingTable component.
 */
const RankingTable = ({
    filteredData,
    selectedPortfolio,
    onRowMouseOver,
    onRowMouseOut,
    onRowClick,
    onKebabClick,
    selectedRow,
    tableVirtuosoProps
}) => {
    const theme = useTheme();

    const rowSelection =
        selectedRow || (selectedPortfolio?.isClicked && selectedPortfolio);

    return (
        <Box data-testid='ebabRankingTable'>
            {!filteredData ? (
                <LinearProgress />
            ) : (
                <TableVirtuoso
                    data={filteredData}
                    data-analyticsid='ebab-table'
                    components={TableComponents}
                    context={{
                        onRowMouseOver,
                        onRowMouseOut,
                        onRowClick,
                        rowSelection: rowSelection
                    }}
                    fixedHeaderContent={FixedHeaderContent}
                    fixedFooterContent={FixedFooterContent}
                    increaseViewportBy={20}
                    itemContent={(_index, row) =>
                        ItemContent(
                            _index,
                            row,
                            getRowStyleStatus(row, rowSelection),
                            onKebabClick,
                            theme
                        )
                    }
                    {...tableVirtuosoProps}
                />
            )}
        </Box>
    );
};
export default RankingTable;

RankingTable.propTypes = {
    filteredData: PropTypes.array,
    selectedPortfolio: PropTypes.object,
    onRowMouseOver: PropTypes.func,
    onRowMouseOut: PropTypes.func,
    onRowClick: PropTypes.func,
    onKebabClick: PropTypes.func,
    selectedRow: PropTypes.object,
    tableVirtuosoProps: PropTypes.object
};
